import { BLUE_200, BOX_SHADOW_XS, CORAL_700, GRAY_400, GRAY_900, WHITE } from 'constants/styling/theme'
import { Badge, Box, Divider, Stack, Typography } from '@mui/material'
import { PendingWorkspaceInvitationItem, PendingWorkspaceInvitationItemVariant } from 'components/common/TopBar/PendingWorkspaceInvitationItem'

import AddIcon from '@mui/icons-material/Add'
import { AnalyticsEvent } from 'utils/analytics'
import { FC, useCallback, useState } from 'react'
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined'
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined'
import { MUIBadge } from 'components/common/MUIBadge'
import { MUIDivider } from 'components/common/MUIDivider'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { Path } from 'constants/router'
import { PropstackLinkedBadge, PropstackWorkspaceMark } from 'components/common/PropstackLinkedBadge'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { WorkspaceRole } from 'models/user'
import { getSeatTypeAccessLabelKey } from 'constants/workspaces'
import { useWorkspaceList } from 'utils/hooks'

/**
 * @interface Props for WorkspaceDropdown component
 */
export interface Props {
  /** Enriched analytics event handler that includes userEmail and organizationId */
  handleLogAnalyticsEvent: (event: string, options?: {}) => void
}

/**
 * @component WorkspaceDropdown displayed TopBar
 * @example <WorkspaceDropdown />
 */
export const WorkspaceDropdown: FC<Props> = ({ handleLogAnalyticsEvent }) => {
  const { t } = useTranslation(['workspace_profile'])
  const navigate = useNavigate()

  const {
    displayedWorkspace,
    activeWorkspaceList,
    isWorkspacePending,
    isWorkspaceResolved,
    hasPendingInvitations,
    selectWorkspace,
    getPropstackMembership
  } = useWorkspaceList()

  const [isOpen, setIsOpen] = useState(false)
  // Popovers portal outside this dropdown's ClickAwayListener, so suppress close while any is open (QA-303).
  const [openPopovers, setOpenPopovers] = useState(0)
  const handlePopoverToggle = useCallback((open: boolean) => setOpenPopovers((count) => (open ? count + 1 : Math.max(0, count - 1))), [])

  return (
    <MUIDropdown
      open={isOpen}
      onClose={() => { if (openPopovers === 0) setIsOpen(false) }}
      button={(_isOpen, action) => (
        <Badge
          overlap="rectangular"
          variant="dot"
          invisible={!hasPendingInvitations}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ '& .MuiBadge-badge': { backgroundColor: CORAL_700, top: '.2rem', right: '.6rem' } }}
        >
          <Stack
            direction="row"
            alignItems="center"
            height="2.4rem"
            onClick={(e) => {
              if (isOpen) return setIsOpen(false)
              handleLogAnalyticsEvent(AnalyticsEvent.WORKSPACE_DROPDOWN_OPENED, {})
              setIsOpen(true)
              action(e)
            }}
            sx={{ cursor: 'pointer' }}
          >

            <MUIDivider margin={12} orientation="vertical" />

            {displayedWorkspace && <PropstackWorkspaceMark workspace={getPropstackMembership(displayedWorkspace.id)} />}

            <Typography variant="text-md" fontWeight={550} color={GRAY_900}>{displayedWorkspace?.name ?? t(isWorkspaceResolved ? 'missing_workspace' : 'loading_workspaces')}</Typography>

            {displayedWorkspace && isWorkspacePending(displayedWorkspace) &&
              <Stack marginLeft={1.5}>
                <MUIBadge color="blue" size="sm" label={t('pending_invitation')} />
              </Stack>
            }

            <Box padding=".6rem">
              {isOpen ?
                <KeyboardArrowUpOutlinedIcon fontSize="large" sx={{ color: GRAY_900 }} />
                :
                <KeyboardArrowDownOutlinedIcon fontSize="large" sx={{ color: GRAY_900 }} />
              }
            </Box>

          </Stack>
        </Badge>
      )}
    >
      <Stack
        sx={{
          boxSizing: 'border-box',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          background: WHITE,
          padding: '.8rem',
          border: `1px solid ${GRAY_400}`,
          borderRadius: '.8rem',
          boxShadow: BOX_SHADOW_XS
        }}
      >

        <Stack
          sx={{
            maxHeight: '26rem',
            overflowY: 'auto',
            gap: '.8rem',
            width: '100%',
          }}
        >
          {activeWorkspaceList.map((workspaceItem) => {
            const isPending = isWorkspacePending(workspaceItem)

            if (isPending) {
              return (
                <PendingWorkspaceInvitationItem
                  key={workspaceItem.id}
                  workspace={workspaceItem}
                  variant={PendingWorkspaceInvitationItemVariant.DESKTOP}
                />
              )
            }

            return (
              <Stack
                key={workspaceItem.id}
                gap={2}
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                padding=".8rem"
                width="100%"
                borderRadius=".4rem"
                onClick={() => {
                  if (workspaceItem.id !== displayedWorkspace?.id) {
                    handleLogAnalyticsEvent(AnalyticsEvent.WORKSPACE_SWITCHED, {
                      sourceWorkspaceId: displayedWorkspace?.id,
                      destinationWorkspaceId: workspaceItem.id,
                    })
                  }
                  selectWorkspace(workspaceItem)
                  setIsOpen(false)
                  navigate(Path.INDEX)
                }}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: workspaceItem.id === displayedWorkspace?.id ? BLUE_200 : 'transparent',
                  flexShrink: 0,
                }}
              >

                <Stack
                  gap={1}
                  flexDirection="row"
                  alignItems="center"
                  minWidth={0}
                >

                  <Typography
                    variant="text-md"
                    height="2.4rem"
                    color={GRAY_900}
                    fontWeight={350}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                    }}
                  >
                    {workspaceItem.name}
                  </Typography>

                  <PropstackLinkedBadge workspace={getPropstackMembership(workspaceItem.id)} isAdmin={workspaceItem.role === WorkspaceRole.ADMIN} onPopoverToggle={handlePopoverToggle} />

                </Stack>

                <MUIBadge color="gray" size="sm" label={t(getSeatTypeAccessLabelKey(workspaceItem.seatType))} sx={{ flexShrink: 0 }} />

              </Stack>
            )
          })}
        </Stack>

        {displayedWorkspace && <Divider sx={{ width: 'calc(100% + 1.6rem)', marginLeft: '-.8rem', borderColor: GRAY_400 }} />}

        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          padding=".8rem"
          width="100%"
          borderRadius=".4rem"
          onClick={() => {
            handleLogAnalyticsEvent(AnalyticsEvent.WORKSPACE_CREATION_INITIATED, { currentWorkspaceId: displayedWorkspace?.id })
            navigate(Path.WORKSPACE_CREATION)
          }}
          sx={{ cursor: 'pointer' }}
        >
          <AddIcon sx={{ color: GRAY_900, fontSize: '2.4rem' }} />
          <Typography variant="text-md" color={GRAY_900} fontWeight={350}>{t('create_new_workspace')}</Typography>
        </Stack>

      </Stack>
    </MUIDropdown>
  )
}
